import Debug from '../../helpers/debug.js';
import ora from 'ora';
import AWS from 'aws-sdk';
import Table from 'cli-table';
import descriptions from '../../helpers/descriptions.js';
import { pr, printJSON, themed } from '../../helpers/print.js';
import { genCredentialsOnClientApi, getTenants, getLinkedSources, getSupportedSources } from '../../helpers/api.js';
import { buildChunks } from '../../helpers/utils.js';

const debug = Debug('commands:tenants:custom-etl');

const command = 'custom-etl';

const desc = 'List tenants with custom ETL in a specific flow';

const builder = async (yargs) => {
  debug('builder', command);

  return yargs
    .option('tenant', descriptions.options['tenant'].config)
    .option('flow', descriptions.options['flow'].config)
    .demandOption('flow', descriptions.options['flow'].demandText);
};

const processTenant = async ({ debug, baseUri, apikey, env, flow, tenant, supportedSources }) => {
  // Get all linked sources of the tuple (env, flow, tenant)
  const linkedSources = await getLinkedSources({
    debug,
    baseUri,
    env,
    flow,
    tenant,
    apikey,
  });

  let result = null;

  for (const { tap } of [...linkedSources, ...supportedSources]) {
    // Generate AWS credentials in order to read the S3 tap folder
    // Note(davi): I'm using "etl-download" because it's a "read ETL" operation
    const { accessKeyId, secretAccessKey, sessionToken } = await genCredentialsOnClientApi({
      debug,
      baseUri,
      task: 'etl-download',
      env,
      tenant,
      flow,
      tap,
      apikey,
    });

    const s3 = new AWS.S3({
      accessKeyId,
      secretAccessKey,
      sessionToken,
    });

    const keyPrefix = `${tenant}/flows/${flow}/taps/${tap}/etl/`;

    const params = {
      Bucket: env,
      Prefix: keyPrefix,
    };

    // List all files inside the S3 tap folder
    const { Contents: s3Files } = await s3.listObjectsV2(params).promise();

    // Check if either "etl.py" or "etl.ipynb" exist
    const hasCustomEtl = s3Files.some(({ Key }) => Key.endsWith('etl.py') || Key.endsWith('etl.ipynb'));

    if (hasCustomEtl) result = tenant;

    // If it's true, we can break already
    if (result !== null) break;
  }

  return result;
};

const handler = async (argv) => {
  debug('handler', command, argv);

  const { hg, json, apikey, env, flow } = argv;

  let message;
  let spinner = ora();

  try {
    message = themed(`Retrieving tenants for environment ${themed(env, 'info')}`);

    !json && spinner.start(themed(`In progress: ${message}...`, 'secondary'));

    const [allTenants, supportedSources] = await Promise.all([
      getTenants({
        debug,
        baseUri: hg.clientApiBaseUri,
        apikey,
        env,
      }),
      getSupportedSources({
        debug,
        baseUri: hg.clientApiBaseUri,
        apikey,
        env,
        flow,
      })
    ]);

    !json && spinner.succeed(themed(`Finished: ${message}.`, 'secondary'));

    let tenantsWithCustomEtl = [];

    message = themed(`Querying for custom ETL scripts for flow ${themed(flow, 'info')}`);

    !json && spinner.start(themed(`In progress: ${message}...`, 'secondary'));

    // Break the tenants array in to an array of arrays so we can make
    // parallel calls and make it faster
    for (const tenants of buildChunks(allTenants)) {
      const maybeTenantsWithCustomEtl = await Promise.all(
        tenants.map((tenant) =>
          processTenant({
            debug,
            baseUri: hg.clientApiBaseUri,
            apikey,
            env,
            flow,
            tenant,
            supportedSources,
          })
        )
      );

      // Note(davi): we need the `.filter(Boolean)` because sometimes
      // the function above returns `null`
      tenantsWithCustomEtl = tenantsWithCustomEtl.concat(maybeTenantsWithCustomEtl.filter(Boolean));
    }

    !json && spinner.succeed(themed(`Finished: ${message}.`, 'secondary'));

    if (json) {
      printJSON(tenantsWithCustomEtl);
    } else {
      // generate results table
      const table = new Table({ head: ['Tenant ID'] });
      table.push(...tenantsWithCustomEtl.map((item) => [item]));

      // print results
      console.log(table.toString());
    }
  } catch (err) {
    if (json) {
      printJSON({ status: 'error', error: err });
    } else {
      spinner.fail(themed(`Error: ${message}.`, 'secondary'));
      pr(themed(`Message: ${themed(err.message)}`, 'secondary'));
      debug(err);
      if (err && err.response && err.response.data) {
        debug('response', err.response.data);
      }
    }
    process.exit(1);
  }
};

export { command, desc, builder, handler };
